/**
 * Project naming intelligence - a concise product identity from the first
 * prompt, deterministically, with no LLM and no ACU cost.
 *
 * Replaces the raw-prompt truncation that produced titles like
 * "A customer support desk. Cu..." (the live defect). Order of authority:
 *
 *   1. An EXPLICIT product name in the prompt ("an app called Mira",
 *      "Create Nuvra, a ...") is preserved exactly.
 *   2. Otherwise the first clause is distilled into a concise, title-cased
 *      noun phrase in the founder's own words ("Build a customer support
 *      desk. Customers submit..." -> "Customer Support Desk").
 *   3. When the clause cannot yield a usable phrase, the intent classifier's
 *      domain provides a truthful working identity ("CRM", "Hotel Booking").
 *   4. Only then the neutral fallback "New Project".
 *
 * The result is a WORKING identity: the founder can rename at any time, and
 * nothing here ever overwrites a user-chosen name (naming runs only at
 * project creation).
 */

import { analyzeProductIntent } from "@/ai/productManager/intentAnalyzer";

export interface ProjectIdentity {
  name:   string;
  source: "explicit" | "derived" | "domain" | "fallback";
}

const MAX_WORDS = 6;
const MAX_CHARS = 48;

/** Leading filler tokens stripped iteratively from the first clause. */
const LEADING_FILLER = new Set([
  "build", "create", "make", "design", "develop", "generate", "start",
  "please", "i", "we", "want", "need", "would", "like", "to", "me", "my",
  "us", "our", "a", "an", "the", "new", "simple", "full", "complete",
  "help", "can", "you",
]);

/** Everything after these connectors is detail, not identity. */
const CONNECTOR_RE = /\s+(?:where|that|which|with|so that|so|to let|to help|for my|for our|for the|in which|allowing)\s+.*$/i;

/** Words kept lowercase inside a title unless first/last. */
const MINOR_WORDS = new Set(["and", "or", "of", "for", "the", "a", "an", "to", "in", "on"]);

function titleCase(phrase: string): string {
  const words = phrase.split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i > 0 && i < words.length - 1 && MINOR_WORDS.has(lower)) return lower;
      // Preserve interior capitals the founder typed (POS, HR, TicketFlow).
      if (/[A-Z]/.test(w.slice(1))) return w;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function capWords(phrase: string): string {
  let words = phrase.split(/\s+/).filter(Boolean).slice(0, MAX_WORDS);
  let out = words.join(" ");
  while (out.length > MAX_CHARS && words.length > 1) {
    words = words.slice(0, -1);
    out = words.join(" ");
  }
  return out.length > MAX_CHARS ? out.slice(0, MAX_CHARS).trim() : out;
}

/** An explicit product name the founder stated ("called Mira", "Create Nuvra, ..."). */
function detectExplicitName(prompt: string): string | null {
  // "an app called Mira" / "named TicketFlow" / 'titled "ResolveHub"'
  const called = prompt.match(/\b(?:called|named|titled)\s+["'“]?([A-Za-z][A-Za-z0-9'&-]*(?:\s+[A-Z][A-Za-z0-9'&-]*){0,3})["'”]?/);
  if (called?.[1]) return called[1].trim();

  // "Create Nuvra, a platform..." / "Build Mira." - a verb followed directly by
  // a capitalized proper noun that is NOT a sentence continuation.
  const direct = prompt.match(/^\s*(?:[Bb]uild|[Cc]reate|[Mm]ake|[Ll]aunch)\s+([A-Z][A-Za-z0-9'&-]{1,30})\s*(?:[,.:;\n]|$)/);
  if (direct?.[1]) return direct[1].trim();

  return null;
}

/** Distill the first clause into a concise noun phrase in the founder's words. */
function deriveFromClause(prompt: string): string | null {
  const clause = prompt.trim().split(/[.!?\n]/)[0] ?? "";
  let words = clause.split(/\s+/).filter(Boolean);

  // Strip leading filler iteratively ("Build a customer support desk" ->
  // "customer support desk"; "I want a tool to ..." collapses its whole head).
  while (words.length > 0 && LEADING_FILLER.has(words[0].toLowerCase().replace(/[^a-z]/gi, ""))) {
    words = words.slice(1);
  }
  let phrase = words.join(" ");

  // Cut trailing detail after connector words.
  phrase = phrase.replace(CONNECTOR_RE, "").trim();
  // Drop trailing punctuation/qualifiers.
  phrase = phrase.replace(/[,;:]+$/g, "").trim();

  if (!phrase) return null;
  const capped = capWords(phrase);
  // A one-character or purely generic residue is not an identity.
  if (capped.length < 3) return null;
  if (/^(app|application|website|site|tool|platform|system|software|product)$/i.test(capped)) return null;
  return titleCase(capped);
}

/** Readable identity for a classified domain when the clause is unusable. */
const DOMAIN_IDENTITY: Record<string, string> = {
  "CRM":                "CRM",
  "Hospitality":        "Hotel Booking",
  "Restaurant":         "Restaurant POS",
  "Faith & Nonprofit":  "Community Management",
  "Legal":              "Legal Practice Manager",
  "Logistics":          "Logistics Platform",
  "Banking":            "Banking Dashboard",
  "Events":             "Event Booking",
  "ERP & Operations":   "Operations Platform",
  "Marketplace":        "Marketplace",
  "Support CRM":        "Support Desk",
  "Customer Success":   "Customer Success",
  "Project Management": "Project Tracker",
  "HR & People":        "HR Platform",
  "Finance":            "Accounting Platform",
  "Healthcare":         "Care Management",
  "Education":          "Learning Platform",
  "Real Estate":        "Property Manager",
  "Fitness & Wellness": "Fitness Studio",
  "E-commerce":         "Online Store",
  "Social & Community": "Community App",
  "Analytics":          "Analytics Dashboard",
  "Content Management": "Content Studio",
};

/**
 * Derive the project's working identity from the first prompt.
 * Deterministic, synchronous, LLM-free; never returns a raw prompt slice.
 */
/**
 * Make the human-facing project name unique within the current owner's project set.
 * Names remain readable; the stable project key continues to carry the collision-resistant
 * identifier used by routing/storage. Comparison is case-insensitive.
 */
export function makeUniqueProjectName(baseName: string, existingNames: string[]): string {
  const base = (baseName || "New Project").trim() || "New Project";
  const used = new Set(existingNames.map(n => n.trim().toLocaleLowerCase()).filter(Boolean));
  if (!used.has(base.toLocaleLowerCase())) return base;

  for (let n = 2; n < 10000; n += 1) {
    const suffix = ` ${n}`;
    const candidate = base.length + suffix.length <= MAX_CHARS
      ? `${base}${suffix}`
      : `${base.slice(0, Math.max(1, MAX_CHARS - suffix.length)).trim()}${suffix}`;
    if (!used.has(candidate.toLocaleLowerCase())) return candidate;
  }

  return `${base.slice(0, Math.max(1, MAX_CHARS - 7)).trim()} 513433`;
}

export function deriveProjectIdentity(prompt: string): ProjectIdentity {
  const trimmed = (prompt ?? "").trim();
  if (!trimmed) return { name: "New Project", source: "fallback" };

  const explicit = detectExplicitName(trimmed);
  if (explicit) return { name: explicit, source: "explicit" };

  const derived = deriveFromClause(trimmed);
  if (derived) return { name: derived, source: "derived" };

  const intent = analyzeProductIntent(trimmed);
  const domainName = DOMAIN_IDENTITY[intent.domain];
  if (domainName) return { name: domainName, source: "domain" };

  return { name: "New Project", source: "fallback" };
}
